import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  InlineNotification,
  Loading,
  Stack,
  Tile,
} from "@carbon/react";
import { CheckmarkFilled } from "@carbon/icons-react";
import type { ImportJobResponse } from "../../types";

type Props = {
  job: ImportJobResponse | undefined;
};

/**
 * The last step: the import is running, has finished, or has failed.
 *
 * Nothing on the review screens survives past here, so this is the whole page while the job runs.
 */
const ImportJobStatus = ({ job }: Props) => {
  const { t } = useTranslation();
  const done = job?.status === "COMPLETED";
  const failed = job?.status === "FAILED";

  return (
    <Tile className="pr-import__panel pr-import__job">
      {!done && !failed && (
        <Stack gap={5}>
          <Loading
            withOverlay={false}
            small
            description={t("purchaseRequests.import.creating")}
          />
          <h2 className="pr-import__title">
            {t("purchaseRequests.import.creating")}
          </h2>
        </Stack>
      )}

      {done && (
        <Stack gap={5}>
          <CheckmarkFilled
            size={32}
            className="pr-import__job-icon"
            aria-hidden="true"
          />
          <h2 className="pr-import__title">
            {t("purchaseRequests.import.done")}
          </h2>
          <p className="pr-import__hint">
            {t("purchaseRequests.import.doneDetail", {
              items: job?.itemsCount ?? 0,
            })}
          </p>
          <div>
            <Button
              as={Link}
              to={
                job?.purchaseRequestId
                  ? `/purchase-requests/${job.purchaseRequestId}`
                  : "/purchase-requests"
              }
            >
              {t("purchaseRequests.import.seeRequests")}
            </Button>
          </div>
        </Stack>
      )}

      {failed && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.failed")}
          subtitle={job?.errorMessage ?? ""}
        />
      )}
    </Tile>
  );
};

export default ImportJobStatus;
